import { SaasSettingsView } from "../../settings";
import { formatSectionTitle, formatUserFirstName } from "../domain/shellFormatters";
import { formatTimeOnly } from "../domain/time";
import type { AgendaState, ShellContentProps } from "../domain/shellTypes";

export function ShellContent({
  state,
  setupState,
  agendaState,
  activeSection,
  customersState,
  bookingPolicyState
}: ShellContentProps) {
  if (state.status === "idle") {
    return <ShellMessage title="Aguardando sessao" message="A area de trabalho da agenda aparecera aqui depois da primeira consulta segura." />;
  }

  if (state.status === "loading") {
    return <ShellMessage title="Carregando area de trabalho" message="Estamos preparando sua area de trabalho da agenda." live />;
  }

  if (state.status === "error") {
    return (
      <div className="authshell__panel authshell__panel--error" role="alert">
        <strong>{state.title}</strong>
        <span>{state.message}</span>
        {state.retryable && <small>Tente novamente em instantes.</small>}
      </div>
    );
  }

  if (state.status === "unauthenticated") {
    return <ShellMessage title="Sessão necessária" message="Entre novamente para acessar a área logada." />;
  }

  const setup = setupState.status === "success" || setupState.status === "saving" || setupState.status === "error"
    ? setupState.setup
    : undefined;

  if (activeSection === "agenda") {
    return <SaasAgendaView agendaState={agendaState} />;
  }

  if (activeSection === "customers") {
    return <SaasCustomersView customersState={customersState} />;
  }

  if (activeSection === "settings") {
    return <SaasSettingsView bookingPolicyState={bookingPolicyState} setup={setup} />;
  }

  if (activeSection === "conversations" || activeSection === "services" || activeSection === "team" || activeSection === "finance" || activeSection === "subscription" || activeSection === "insights") {
    return <SaasModulePlaceholder title={formatSectionTitle(activeSection)} />;
  }

  return <SaasHomeView userEmail={state.boot.user.email} agendaState={agendaState} />;
}

function ShellMessage({ title, message, live }: { title: string; message: string; live?: boolean }) {
  return (
    <div className="authshell__panel authshell__panel--muted" aria-live={live ? "polite" : undefined}>
      <strong>{title}</strong>
      <span>{message}</span>
    </div>
  );
}

function SaasHomeView({ userEmail, agendaState }: { userEmail: string; agendaState: AgendaState }) {
  const firstName = formatUserFirstName(userEmail);
  const appointments = agendaState.status === "success" ? agendaState.appointments.slice(0, 3) : [];
  const rows = appointments.length > 0
    ? appointments.map((appointment, index) => ({
      time: formatTimeOnly(appointment.start_time),
      name: appointment.customer_name || "Cliente",
      service: "Atendimento",
      dimmed: index === 2
    }))
    : [
      { time: "09:00", name: "Ana Lima", service: "Corte Feminino", dimmed: false },
      { time: "10:00", name: "Carlos Mendes", service: "Barba Completa", dimmed: false },
      { time: "11:30", name: "Maria Santos", service: "Coloração", dimmed: true }
    ];

  return (
    <div className="authshell__saas authshell__saas--home" aria-live="polite">
      <section className="authshell__saasHero">
        <p>Bom dia, {firstName}</p>
        <span>Sexta-feira, 22 de Maio</span>
      </section>
      <section className="authshell__metricGrid" aria-label="Resumo operacional">
        <SaasMetric title="Faturamento" value="R$1.250" detail="+12% vs. média" tone="positive" />
        <SaasMetric title="Agendamentos" value="12" detail="8 confirmados" />
        <SaasMetric title="Ocupação" value="85%" detail="2 horários livres" />
        <SaasMetric title="Meu link" value="thh.go/admin" detail="Copiar link" />
      </section>
      <section className="authshell__quickActions" aria-label="Ações rápidas">
        <p>Ações rápidas</p>
        <div>
          <button type="button"><span>+</span>Agendar</button>
          <button type="button"><span>+</span>Cliente</button>
          <button type="button"><span>+</span>Conversa</button>
          <button type="button"><span>↗</span>Compartilhar</button>
        </div>
      </section>
      <section className="authshell__saasMainGrid">
        <div className="authshell__appointmentsPanel">
          <h2>Próximos atendimentos</h2>
          <SaasAppointmentList rows={rows} />
        </div>
        <aside className="authshell__rightRail">
          <article className="authshell__shareCard">
            <span className="authshell__shareIcon">↗</span>
            <h3>Compartilhe sua agenda</h3>
            <p>Coloque o link na sua bio do Instagram e atraia mais reservas.</p>
            <button type="button">Copiar Link</button>
          </article>
          <article className="authshell__insightCard">
            <h3>Insights</h3>
            <div>
              <p>O horário das 14h costuma ficar vazio às sextas. Deseja enviar uma campanha relâmpago?</p>
              <strong>Recomendar horários</strong>
            </div>
          </article>
        </aside>
      </section>
    </div>
  );
}

function SaasAgendaView({ agendaState }: { agendaState: AgendaState }) {
  const appointment = agendaState.status === "success" ? agendaState.appointments[0] : undefined;

  return (
    <div className="authshell__saas authshell__saas--agenda" aria-label="Agenda">
      <section className="authshell__calendarShell">
        <header>
          <h2>22 de Maio, Sexta-feira</h2>
          <div>
            <button type="button" aria-label="Buscar">⌕</button>
            <button type="button">+ Novo</button>
          </div>
        </header>
        <div className="authshell__calendarGrid">
          {["09:00", "10:00", "11:00", "12:00", "13:00"].map((time, index) => (
            <div className="authshell__calendarRow" key={time}>
              <span>{time}</span>
              <div>
                {index === 0 && (
                  <article>
                    <strong>{appointment?.customer_name || "Ana Lima"}</strong>
                    <small>{appointment ? "Atendimento" : "Corte Feminino"} • 1h</small>
                  </article>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function SaasCustomersView({ customersState }: { customersState: ShellContentProps["customersState"] }) {
  const customers = customersState.status === "success" && customersState.result.data.length > 0
    ? customersState.result.data.slice(0, 3).map((customer) => ({
      initial: customer.name.trim()[0] || "C",
      name: customer.name,
      phone: customer.phone,
      last: "ha 2 dias"
    }))
    : [
      { initial: "A", name: "Ana Lima", phone: "(11) 99123-4567", last: "ha 2 dias" },
      { initial: "C", name: "Carlos Mendes", phone: "(11) 98765-4321", last: "ha 5 dias" },
      { initial: "M", name: "Maria Santos", phone: "(11) 91111-2222", last: "ha 1 semana" }
    ];

  return (
    <div className="authshell__saas authshell__saas--customers" aria-label="Clientes">
      <header className="authshell__sectionHeader">
        <h2>Clientes</h2>
        <button type="button">+ Novo</button>
      </header>
      <label className="authshell__customerSearch">
        <span>⌕</span>
        <input placeholder="Buscar clientes..." />
      </label>
      <div className="authshell__customerList">
        {customers.map((customer) => (
          <article className="authshell__customerRow" key={customer.name}>
            <span>{customer.initial.toUpperCase()}</span>
            <div>
              <strong>{customer.name}</strong>
              <small>{customer.phone}</small>
            </div>
            <em>{customer.last}</em>
          </article>
        ))}
      </div>
    </div>
  );
}

function SaasModulePlaceholder({ title }: { title: string }) {
  return (
    <div className="authshell__saas authshell__saas--placeholder" aria-label={title}>
      <span aria-hidden="true">▣</span>
      <h2>{title}</h2>
      <p>Este módulo está sendo preparado com a filosofia THH GO.</p>
    </div>
  );
}

function SaasMetric({ title, value, detail, tone }: { title: string; value: string; detail: string; tone?: "positive" }) {
  return (
    <article className="authshell__metricCard">
      <p>{title}</p>
      <strong>{value}</strong>
      <span className={tone === "positive" ? "is-positive" : ""}>{detail}</span>
    </article>
  );
}

function SaasAppointmentList({ rows }: { rows: Array<{ time: string; name: string; service: string; dimmed: boolean }> }) {
  return (
    <div className="authshell__appointmentRail">
      {rows.map((row) => (
        <article className={`authshell__saasAppointment ${row.dimmed ? "is-dimmed" : ""}`} key={`${row.time}-${row.name}`}>
          <time>{row.time}</time>
          <div>
            <strong>{row.name}</strong>
            <span>{row.service}</span>
          </div>
          <i aria-hidden="true" />
        </article>
      ))}
    </div>
  );
}
